import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Calendar, Clock, FileText, User, AlertCircle } from 'lucide-react';
import axios from 'axios';

const PreExamGate = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [testDetails, setTestDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [candidateInfo, setCandidateInfo] = useState({
    name: '',
    candidateNo: '',
    email: '',
    whatsapp: '',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchTestDetails();
  }, [testId]);

  const fetchTestDetails = async () => {
    try {
      const response = await axios.get('/student/tests');
      const test = response.data.find(t => t.id === testId);
      if (!test) {
        toast.error('Test not found');
        navigate('/student');
        return;
      }
      setTestDetails(test);
    } catch (error) {
      toast.error('Failed to fetch test details');
      navigate('/student');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTest = async () => {
    if (!candidateInfo.name.trim() || !candidateInfo.candidateNo.trim() || !candidateInfo.email.trim()) {
      toast.error('Please fill in all required information');
      return;
    }

    // WhatsApp validation - must be 11 digits if provided
    if (candidateInfo.whatsapp && !/^[0-9]{11}$/.test(candidateInfo.whatsapp)) {
      toast.error('WhatsApp number must be exactly 11 digits');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`/student/tests/${testId}/start`, {
        test_id: testId,
        candidate_name: candidateInfo.name,
        candidate_no: candidateInfo.candidateNo,
        candidate_email: candidateInfo.email,
        candidate_whatsapp: candidateInfo.whatsapp || null
      });

      toast.success('Test started successfully!');
      navigate(`/exam/writing/${response.data.submission_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start test');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-main-gradient flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-main-gradient flex items-center justify-center p-4">
      <div className="pre-exam-modal">
        <div className="pre-exam-content">
          <div className="pre-exam-header">
            <h2 className="pre-exam-title">Writing Test</h2>
            <p className="pre-exam-subtitle">Please fill up your information</p>
          </div>

          {/* Candidate Information Form */}
          <div className="candidate-form">
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                Candidate Name*
              </label>
              <input
                type="text"
                data-testid="candidate-name-input"
                placeholder="Enter your full name"
                value={candidateInfo.name}
                onChange={(e) => setCandidateInfo({ ...candidateInfo, name: e.target.value })}
                required
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                Candidate No*
              </label>
              <input
                type="text"
                data-testid="candidate-no-input"
                placeholder="Enter candidate number"
                value={candidateInfo.candidateNo}
                onChange={(e) => setCandidateInfo({ ...candidateInfo, candidateNo: e.target.value })}
                required
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                Email*
              </label>
              <input
                type="email"
                data-testid="candidate-email-input"
                placeholder="Enter your email"
                value={candidateInfo.email}
                onChange={(e) => setCandidateInfo({ ...candidateInfo, email: e.target.value })}
                required
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                WhatsApp Number (Optional)
              </label>
              <input
                type="tel"
                data-testid="candidate-whatsapp-input"
                placeholder="01XXXXXXXXX"
                maxLength={11}
                value={candidateInfo.whatsapp}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 11);
                  setCandidateInfo({ ...candidateInfo, whatsapp: value });
                }}
              />
              <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                11-digit number • Result will also be sent via WhatsApp
              </p>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                Date
              </label>
              <input
                type="date"
                data-testid="test-date-input"
                value={candidateInfo.date}
                onChange={(e) => setCandidateInfo({ ...candidateInfo, date: e.target.value })}
              />
            </div>
          </div>

          {/* Test Information */}
          <div className="test-info-section">
            <h3 className="test-info-title">IELTS Academic Writing</h3>
            <ul className="test-info-list">
              <li>Time: {testDetails?.duration_minutes || 60} minutes</li>
              <li>Answer both parts; you may switch between tasks anytime.</li>
              <li>Part 2 counts double in writing score.</li>
              <li style={{ color: '#dc2626' }}>Clock will alert when 10 and 5 minutes remain.</li>
            </ul>
          </div>

          {/* Important Rules Warning */}
          <div style={{
            backgroundColor: '#FEF2F2',
            border: '2px solid #F87171',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ color: '#DC2626', fontSize: '20px' }}>⚠️</span>
              <div>
                <p style={{ 
                  color: '#DC2626', 
                  fontWeight: '700', 
                  fontSize: '14px',
                  marginBottom: '6px'
                }}>
                  Important Rules - Read Carefully!
                </p>
                <ul style={{ 
                  color: '#B91C1C', 
                  fontSize: '13px',
                  margin: '0',
                  paddingLeft: '16px',
                  lineHeight: '1.6'
                }}>
                  <li><strong>First tab switch</strong> = Warning</li>
                  <li><strong>Second tab switch</strong> = Your exam will be <strong>auto-submitted</strong></li>
                  <li>Once submitted, you <strong>cannot retake</strong> this exam</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Start Button */}
          <div className="start-test-section">
            <button
              onClick={handleStartTest}
              disabled={submitting}
              className="start-test-btn"
              data-testid="start-test-button"
            >
              {submitting ? 'Starting Test...' : 'Start Test'}
            </button>
            
            <p className="timer-note">
              Timer will start after you press "Start Test".
            </p>
          </div>
          
          {/* Back Button */}
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <button
              onClick={() => navigate('/student')}
              style={{
                background: 'transparent',
                color: '#64748b',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                padding: '8px 16px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
              data-testid="back-to-dashboard-button"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreExamGate;